(function () {
  const MOBILE_QUERY = '(max-width: 749px)';

  class SDJoinUsGalleryLoop {
    constructor(root) {
      this.root = root;
      this.scroller = root.querySelector('.sd-join-us__gallery-track');
      this.active = false;
      this.jumping = false;
      this.clones = [];
      this.originals = [];
      this.settleTimer = null;

      this.mql = window.matchMedia(MOBILE_QUERY);
      this.onMedia = () => this.sync();
      this.onScroll = () => {
        if (this.jumping) return;
        window.clearTimeout(this.settleTimer);
        this.settleTimer = window.setTimeout(() => this.settle(), 120);
      };

      if (this.mql.addEventListener) {
        this.mql.addEventListener('change', this.onMedia);
      } else if (this.mql.addListener) {
        this.mql.addListener(this.onMedia);
      }
    }

    sync() {
      if (this.mql.matches) {
        this.enable();
      } else {
        this.disable();
      }
    }

    getOriginalItems() {
      if (!this.scroller) return [];

      return Array.from(this.scroller.children).filter(
        (item) =>
          item.classList.contains('sd-join-us__gallery-item') &&
          !item.classList.contains('sd-join-us__gallery-item--clone')
      );
    }

    makeClones() {
      return this.originals.map((item) => {
        const clone = item.cloneNode(true);
        clone.classList.add('sd-join-us__gallery-item--clone');
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('a, button, [tabindex]').forEach((element) => {
          element.setAttribute('tabindex', '-1');
        });
        return clone;
      });
    }

    enable() {
      if (this.active || !this.scroller) return;

      this.originals = this.getOriginalItems();
      if (this.originals.length === 0) return;

      this.active = true;

      const leadFragment = document.createDocumentFragment();
      const trailFragment = document.createDocumentFragment();
      const lead = this.makeClones();
      const trail = this.makeClones();

      lead.forEach((clone) => leadFragment.appendChild(clone));
      trail.forEach((clone) => trailFragment.appendChild(clone));

      this.scroller.insertBefore(leadFragment, this.originals[0]);
      this.scroller.appendChild(trailFragment);
      this.clones = lead.concat(trail);

      this.scroller.addEventListener('scroll', this.onScroll, { passive: true });

      this.centerWhenReady(0);
    }

    centerWhenReady(index) {
      const images = Array.from(this.scroller.querySelectorAll('img'));

      const center = () => {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => this.centerOriginal(index));
        });
      };

      const pending = images.filter((image) => !image.complete);

      if (pending.length === 0) {
        center();
        return;
      }

      let loaded = 0;
      const onDone = () => {
        loaded += 1;
        if (loaded >= pending.length) center();
      };

      pending.forEach((image) => {
        image.addEventListener('load', onDone, { once: true });
        image.addEventListener('error', onDone, { once: true });
      });
    }

    disable() {
      if (!this.active) return;

      this.active = false;
      this.scroller.removeEventListener('scroll', this.onScroll);
      window.clearTimeout(this.settleTimer);

      this.clones.forEach((clone) => clone.remove());
      this.clones = [];
      this.scroller.scrollLeft = 0;
    }

    centerCard(card) {
      if (!card || !this.scroller) return;

      const scrollerRect = this.scroller.getBoundingClientRect();
      const cardRect = card.getBoundingClientRect();
      const delta =
        cardRect.left + cardRect.width / 2 - (scrollerRect.left + this.scroller.clientWidth / 2);

      const previous = this.scroller.style.scrollBehavior;
      this.scroller.style.scrollBehavior = 'auto';
      this.scroller.scrollLeft += delta;
      this.scroller.style.scrollBehavior = previous;
    }

    centerOriginal(index) {
      const child = this.scroller.children[this.originals.length + index];
      this.centerCard(child);
    }

    settle() {
      if (!this.active) return;

      const children = Array.from(this.scroller.children);
      const length = this.originals.length;

      if (children.length < length * 3) return;

      const scrollerRect = this.scroller.getBoundingClientRect();
      const center = scrollerRect.left + this.scroller.clientWidth / 2;

      let bestIndex = 0;
      let bestDistance = Infinity;

      children.forEach((child, index) => {
        const rect = child.getBoundingClientRect();
        const distance = Math.abs(rect.left + rect.width / 2 - center);
        if (distance < bestDistance) {
          bestDistance = distance;
          bestIndex = index;
        }
      });

      const setWidth =
        children[length].getBoundingClientRect().left - children[0].getBoundingClientRect().left;

      if (bestIndex < length) {
        this.jumpBy(setWidth);
      } else if (bestIndex >= length * 2) {
        this.jumpBy(-setWidth);
      }
    }

    jumpBy(delta) {
      if (!delta) return;

      this.jumping = true;
      const previous = this.scroller.style.scrollBehavior;
      this.scroller.style.scrollBehavior = 'auto';
      this.scroller.scrollLeft += delta;
      this.scroller.style.scrollBehavior = previous;

      window.requestAnimationFrame(() => {
        this.jumping = false;
        if (this.active) this.recenterNearest();
      });
    }

    recenterNearest() {
      if (!this.active) return;

      const children = Array.from(this.scroller.children);
      if (!children.length) return;

      const scrollerRect = this.scroller.getBoundingClientRect();
      const center = scrollerRect.left + this.scroller.clientWidth / 2;

      let best = null;
      let bestDistance = Infinity;

      children.forEach((child) => {
        const rect = child.getBoundingClientRect();
        const distance = Math.abs(rect.left + rect.width / 2 - center);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = child;
        }
      });

      this.centerCard(best);
    }

    onResize() {
      if (!this.active) return;
      this.centerWhenReady(this.getActiveOriginalIndex());
    }

    getActiveOriginalIndex() {
      if (!this.active || this.originals.length === 0) return 0;

      const scrollerRect = this.scroller.getBoundingClientRect();
      const center = scrollerRect.left + this.scroller.clientWidth / 2;

      let bestIndex = 0;
      let bestDistance = Infinity;

      this.originals.forEach((item, index) => {
        const rect = item.getBoundingClientRect();
        const distance = Math.abs(rect.left + rect.width / 2 - center);
        if (distance < bestDistance) {
          bestDistance = distance;
          bestIndex = index;
        }
      });

      return bestIndex;
    }

    destroy() {
      this.disable();

      if (this.mql.removeEventListener) {
        this.mql.removeEventListener('change', this.onMedia);
      } else if (this.mql.removeListener) {
        this.mql.removeListener(this.onMedia);
      }
    }
  }

  const instances = new WeakMap();

  function initGallery(root) {
    if (!root) return;

    const existing = instances.get(root);
    if (existing) {
      existing.destroy();
      instances.delete(root);
    }

    const instance = new SDJoinUsGalleryLoop(root);
    instances.set(root, instance);
    instance.sync();
  }

  function initAll(scope = document) {
    if (!scope.querySelectorAll) return;

    scope.querySelectorAll('[data-sd-join-us-gallery]').forEach(initGallery);
  }

  function onResize() {
    document.querySelectorAll('[data-sd-join-us-gallery]').forEach((root) => {
      const instance = instances.get(root);
      if (instance) instance.onResize();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  window.addEventListener('resize', onResize);

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    if (!event.target.querySelectorAll) return;

    event.target.querySelectorAll('[data-sd-join-us-gallery]').forEach((root) => {
      const instance = instances.get(root);
      if (instance) {
        instance.destroy();
        instances.delete(root);
      }
    });
  });
})();
